'use client';

// The listening room's audio: one song, played through whichever room is on
// screen, before or after its treatment.
//
// The engine is the product's own (@roomtreatment/auralization, the Audio
// preview's engine), and so is the before/after mapping (`buildStates`). What
// differs from the editor's hook (room/audio/useAuralization.ts) is the job.
// There, the curves change under a live session and a debounced rebuild
// follows them. Here, a visitor presses one button and expects music, so a
// press has to fetch the song, build the room's two impulse responses and
// start playing, in that order, from a single click; and switching rooms
// mid-song has to carry on in the new room from the same bar. Those are
// sequences, so they are written as sequences: each is an async run, and a
// newer press or switch retires an older run before it can play anything.
//
// Nothing loads until the first press: the page never pays for audio a
// visitor does not ask to hear.

import { useCallback, useEffect, useRef, useState } from 'react';
import { AuralizationEngine } from '@/lib/audio/engine';
import { buildStates } from '@/components/room/audio/useAuralization';
import type { ABState } from '@/components/upsell/BeforeAfter';
import type { RoomAudioInputs } from './roomAudio';

/** The dry source: a 32 s excerpt of the Audio preview's electronic sample
 *  (0:24-0:56 of public/audio/samples/song-electronic.wav), cut and encoded
 *  once so the homepage fetches ~0.6 MB rather than the 12 MB WAV. */
export const LISTEN_SAMPLE_URL = '/home/listen.mp3';

export type ListenPhase = 'idle' | 'loading' | 'playing' | 'paused' | 'failed';

export interface ListeningAudio {
  phase: ListenPhase;
  /** Start from a press: load and build what is missing, then play `state`. */
  play: (room: RoomAudioInputs, state: ABState) => void;
  pause: () => void;
  /** Before/after, mid-song if playing. */
  setState: (state: ABState) => void;
  /** Another room: rebuilt now if playing (and resumed at the same point),
   *  otherwise on the next press. */
  setRoom: (room: RoomAudioInputs) => void;
  /** Playback position, for a bar that draws itself without re-rendering
   *  the room every frame. Returns an unsubscribe. */
  subscribeProgress: (listener: (fraction: number) => void) => () => void;
}

export function useListeningAudio(): ListeningAudio {
  const engine = useRef<AuralizationEngine | null>(null);
  const [phase, setPhase] = useState<ListenPhase>('idle');
  /** Which room the engine's impulse responses were built for. */
  const built = useRef<RoomAudioInputs | null>(null);
  const source = useRef<Promise<void> | null>(null);
  const state = useRef<ABState>('before');
  const run = useRef(0);
  const listeners = useRef(new Set<(fraction: number) => void>());

  useEffect(() => {
    const eng = new AuralizationEngine({
      onPlayingChange: (playing) => {
        if (playing) setPhase('playing');
      },
      onProgress: (elapsed, duration) => {
        const f = duration > 0 ? elapsed / duration : 0;
        listeners.current.forEach((l) => l(f));
      },
      // A loop: the song is a demo of a room, not a track to finish.
      onEnded: () => eng.play(state.current),
    });
    engine.current = eng;
    return () => {
      run.current += 1;
      eng.dispose();
      engine.current = null;
    };
  }, []);

  /** Engine work runs one at a time. Two builds interleaved would leave the
   *  engine holding whichever room finished last, whatever `built` says. */
  const chain = useRef<Promise<void>>(Promise.resolve());

  /** Fetch the song once, build `room`'s two responses if the engine holds
   *  another room's, then play from where the song was — unless a newer run
   *  has started meanwhile, in which case this one stops at its next step. */
  const start = useCallback((room: RoomAudioInputs) => {
    const token = ++run.current;
    setPhase('loading');
    chain.current = chain.current.then(async () => {
      const eng = engine.current;
      if (!eng || token !== run.current) return;
      try {
        source.current ??= eng.loadSourceUrl(LISTEN_SAMPLE_URL);
        await source.current;
        if (token !== run.current) return;
        if (built.current !== room) {
          const states = buildStates(room.freq, room.rt60, room.nProducts);
          if (!states) throw new Error('This room has no curves to hear.');
          await eng.setStates(states);
          built.current = room;
        }
        if (token !== run.current) return;
        eng.play(state.current);
      } catch {
        if (token !== run.current) return;
        // The fetch or the decode failed: a later press starts from scratch.
        source.current = null;
        setPhase('failed');
      }
    });
  }, []);

  const play = useCallback((room: RoomAudioInputs, next: ABState) => {
    state.current = next;
    // Inside the click: a context created or resumed outside a user gesture
    // stays suspended on iOS, which is silent playback.
    engine.current?.resume();
    start(room);
  }, [start]);

  const pause = useCallback(() => {
    run.current += 1;
    engine.current?.pause();
    setPhase((p) => (p === 'idle' ? p : 'paused'));
  }, []);

  const setState = useCallback((next: ABState) => {
    state.current = next;
    engine.current?.setActiveState(next);
  }, []);

  const setRoom = useCallback((room: RoomAudioInputs) => {
    if (phase !== 'playing' && phase !== 'loading') return;
    engine.current?.pause();
    start(room);
  }, [phase, start]);

  const subscribeProgress = useCallback((listener: (fraction: number) => void) => {
    listeners.current.add(listener);
    return () => { listeners.current.delete(listener); };
  }, []);

  return { phase, play, pause, setState, setRoom, subscribeProgress };
}
